import { Modal, Button, Form, ProgressBar } from 'react-bootstrap';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import config from '../../config';

// Step Components
import BasicRequestInfoForm from './sections/BasicRequestInfoForm';
import PurchaseItemsForm from './sections/PurchaseItemsForm';
import ReviewSubmitForm from './sections/ReviewSubmitForm';

const steps = [
  { title: 'Request Info', component: BasicRequestInfoForm },
  { title: 'Items', component: PurchaseItemsForm },
  { title: 'Review & Submit', component: ReviewSubmitForm },
];

function AddPurchaseModal({ show, onHide, onRequestAdded }) {
  const [step, setStep] = useState(0);
  const [stepSubmitted, setStepSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    required_by_date: '',
    reason: '',
    priority: 'high',
    notes: '',
    items: [
      { product: '', quality: '', approx_quantity: '', quantity_unit: '' }
    ],
  });

  const progress = Math.round(((step + 1) / steps.length) * 100);
  const StepComponent = steps[step].component;

  const handleNext = (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent step changes during submission
    setStepSubmitted(true);

    if (StepComponent.validate && !StepComponent.validate(form)) return;

    setStep(prev => prev + 1);
    setStepSubmitted(false);
  };

  const handlePrev = () => {
    if (submitting) return; // Prevent step changes during submission
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (submitting) return; // Prevent double submission
  
  setSubmitting(true);
  // mark as submitted so step components show validation errors
  setStepSubmitted(true);

  // validate all steps before sending
  const validators = [BasicRequestInfoForm.validate, PurchaseItemsForm.validate];
  for (let i = 0; i < validators.length; i++) {
    const valid = validators[i] ? validators[i](form) : true;
    if (!valid) {
      // navigate to the first invalid step so user can correct
      setStep(i);
      setSubmitting(false);
      return;
    }
  }

  try {
    const token = localStorage.getItem('token');
    const decoded = jwtDecode(token);
    const createdBy = decoded.userId;

    // Transform products from object to ID
    const payload = {
      ...form,
      requested_by: createdBy,
      items: form.items.map(item => ({
        ...item,
        product: typeof item.product === 'object' ? item.product.value : item.product,
      })),
    };

    await axios.post(`${config.API_BASE_URL}/purchase-requests`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // toast.success('Purchase request added!');
    sessionStorage.setItem('requestSuccess', 'true');
    onRequestAdded();
    // close modal - parent should refresh list if needed
    onHide();
  } catch (err) {
    console.error(err);
    toast.error('Failed to submit request');
  } finally {
    setSubmitting(false);
  }
};

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>Add Purchase Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-3">{steps[step].title}</h5>
          <ProgressBar now={progress} label={`${progress}%`} className="mb-4" />
          <StepComponent form={form} setForm={setForm} stepSubmitted={stepSubmitted} />
        </Modal.Body>
        <Modal.Footer>
          {step > 0 && <Button variant="secondary" onClick={handlePrev} disabled={submitting}>Previous</Button>}
          {step < steps.length - 1
            ? <Button variant="primary" onClick={handleNext} disabled={submitting}>Next</Button>
            : <Button type="button" variant="success" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddPurchaseModal;
